import type { Environment } from '../types/Environment';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { getEnvironment } from './getEnvironment';

/**
 * @group Functions
 * @summary <blockquote>Utility function to check if the current environment matches a specific environment using the Result pattern.</blockquote>
 * 
 * @param targetEnvironment - The environment to check against
 * @returns Result containing boolean indicating if current environment matches target, or AppError if environment detection fails
 * 
 * @example
 * ```typescript
 * import { isEnvironment, ENVIRONMENTS } from '@shared/utils';
 * 
 * const result = isEnvironment(ENVIRONMENTS.PRODUCTION);
 * 
 * result.match({
 *   ok: (isProd) => {
 *     if (isProd) {
 *       console.log('Running in production');
 *     }
 *   },
 *   err: (error) => {
 *     console.error(`Environment check failed: ${error.errorMessage}`);
 *   }
 * });
 * ```
 */
export function isEnvironment(targetEnvironment: Environment): Result<boolean, AppError> {
  const envResult = getEnvironment();
  
  return envResult.match({
    ok: (currentEnv) => ok(currentEnv === targetEnvironment),
    err: (error) => err(error)
  });
} 